import { useEffect, useRef } from 'react';
import { useAppSelector } from '../store/hooks';
import { Reminder } from '../types';
import { showNotification, isReminderUpcoming, isReminderRecent } from './notificationUtils';

export const useReminderNotifications = () => {
  const reminders = useAppSelector((state) => state.reminders.items);
  const notifiedRemindersRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();

      reminders.forEach((reminder: Reminder) => {
        if (reminder.completed) return; // Skip completed reminders

        const reminderId = reminder.id;

        // Check for upcoming reminders (within 1 hour)
        if (isReminderUpcoming(reminder, 60)) {
          if (!notifiedRemindersRef.current.has(`upcoming-${reminderId}`)) {
            const timeUntil = Math.round((new Date(`${reminder.date}T${reminder.time || '00:00'}:00`).getTime() - now.getTime()) / (1000 * 60));
            showNotification(`Upcoming Reminder: ${reminder.title}`, {
              body: `In ${timeUntil} minutes`,
              tag: `reminder-${reminderId}`,
              requireInteraction: false,
            });
            notifiedRemindersRef.current.add(`upcoming-${reminderId}`);
          }
        }

        // Check for recent reminders (within 12 hours ago)
        if (isReminderRecent(reminder, 12)) {
          if (!notifiedRemindersRef.current.has(`recent-${reminderId}`)) {
            const hoursAgo = Math.round((now.getTime() - new Date(`${reminder.date}T${reminder.time || '00:00'}:00`).getTime()) / (1000 * 60 * 60));
            showNotification(`Recent Reminder: ${reminder.title}`, {
              body: `Happened ${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`,
              tag: `reminder-${reminderId}`,
              requireInteraction: false,
            });
            notifiedRemindersRef.current.add(`recent-${reminderId}`);
          }
        }
      });
    };

    // Check immediately on mount
    checkReminders();

    // Set up interval to check every minute
    const interval = setInterval(checkReminders, 60 * 1000);

    return () => clearInterval(interval);
  }, [reminders]);

  // Cleanup function to reset notified reminders when reminders change significantly
  useEffect(() => {
    // Reset notifications for reminders that are no longer in the list or are completed
    const currentReminderIds = new Set(reminders.map((r: Reminder) => r.id));
    const newNotified = new Set<string>();

    notifiedRemindersRef.current.forEach(key => {
      const reminderId = key.split('-').slice(1).join('-') as any; // Cast to UUID type
      if (currentReminderIds.has(reminderId)) {
        const reminder = reminders.find((r: Reminder) => r.id === reminderId);
        if (reminder && !reminder.completed) {
          newNotified.add(key);
        }
      }
    });

    notifiedRemindersRef.current = newNotified;
  }, [reminders]);
};
